import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Founding Beta Welcome — shown ONCE per user on first dashboard visit.
 * Elegant, minimalistic. Stored under localStorage('meybra_welcome_seen').
 *
 * Usage: <FoundingWelcome userId={user?.id} role="coach" />
 *
 * Set `force` to true to re-render (admin / debug only).
 */
const FoundingWelcome = ({ userId, role = 'user', force = false }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    // Never show the Founding Welcome during a demo session — the demo bar
    // already communicates context and the welcome would be confusing to a
    // visitor who never signed up.
    if (typeof window !== 'undefined' && window.localStorage?.getItem('meybra_demo_session_id')) return;
    const key = `meybra_welcome_seen_${userId}`;
    const seen = typeof window !== 'undefined' && window.localStorage?.getItem(key);
    if (force || !seen) {
      // Small delay so the dashboard renders behind the modal
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, [userId, force]);

  const close = () => {
    if (userId) {
      try {
        window.localStorage?.setItem(`meybra_welcome_seen_${userId}`, '1');
      } catch (e) { /* ignore */ }
    }
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
          data-testid="founding-welcome-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.3 }}
            className="relative max-w-md w-full bg-card border border-primary/30 rounded-sm p-8 sm:p-10 text-center shadow-2xl shadow-primary/5"
            onClick={(e) => e.stopPropagation()}
            data-testid="founding-welcome-modal"
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-zinc-900/60 border border-border/30 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
              aria-label="Close"
              data-testid="founding-welcome-close"
            >
              <X size={14} />
            </button>

            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-3 py-1 mb-6">
              <Sparkles size={11} className="text-primary" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-primary">Founding Beta</span>
            </div>

            <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide leading-tight mb-4">
              You are one of <br />
              <span className="text-primary">Meybra's founding users</span>
            </h2>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-7 max-w-sm mx-auto">
              Thank you for helping shape the future of connected player development.
            </p>

            <p className="text-xs text-zinc-500 mb-8 max-w-xs mx-auto leading-relaxed">
              Complimentary access during the validation phase — your feedback directly shapes what Meybra becomes.
            </p>

            <Button
              onClick={close}
              className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider text-sm font-bold w-full sm:w-auto px-8"
              data-testid="founding-welcome-cta"
            >
              Enter the platform
              <ArrowRight size={14} className="ml-2" />
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FoundingWelcome;
